import { useEffect, useState, useRef } from "react";
import socket from "../../socket/socket";
import { trackDriver, trackDrivers } from "../../services/orderTrackService";

const useDrivers = (branchSwitch, selectedDriver, setSelectedDriver) => {
  const [drivers, setDrivers] = useState([]);
  const [followTargetId, setFollowTargetId] = useState(null);

  const lastPositionsRef = useRef({});
  const fetchCache = useRef(new Set());

  /** ---------------- FETCH ALL DRIVERS ---------------- **/
  const fetchDrivers = async () => {
    try {
      const res = await trackDrivers(branchSwitch);

      if (res.data.code === 1) {
        const raw = res.data.data || [];

        raw.forEach((d) => {
          if (d.location) lastPositionsRef.current[d.driverId] = d.location;
        });

        setDrivers(raw);
      }
    } catch (err) {
      console.error("Fetch Drivers Error:", err);
    }
  };

  /** ---------------- FETCH SINGLE DRIVER ---------------- **/
  const fetchSingleDriver = async (driverId) => {
    if (fetchCache.current.has(driverId)) return;
    fetchCache.current.add(driverId);

    try {
      const res = await trackDriver(branchSwitch, driverId);

      if (res.data?.data) {
        const fullDriver = res.data.data;
        const orders = fullDriver?.orders;

        // If driver has NO orders → remove from list
        if (!orders || !Array.isArray(orders) || orders.length === 0) {
          setDrivers((prev) => prev.filter((d) => d.driverId !== driverId));
          return;
        }

        // Otherwise → update or insert
        lastPositionsRef.current[driverId] = fullDriver.location;

        setDrivers((prev) => {
          const exists = prev.some((d) => d.driverId === driverId);
          if (exists) {
            return prev.map((d) =>
              d.driverId === driverId ? { ...d, ...fullDriver } : d
            );
          }
          return [...prev, fullDriver];
        });
      } else {
        setDrivers((prev) => prev.filter((d) => d.driverId !== driverId));
        return;
      }
    } catch (err) {
      console.error("Single Driver Fetch Failed:", err);
    } finally {
      setTimeout(() => fetchCache.current.delete(driverId), 3000);
    }
  };

  /** ---------------- FOLLOW MODE ---------------- **/
  const updateSelectedDriver = async (driverId, follow = false) => {
    try {
      const res = await trackDriver(branchSwitch, driverId);

      if (res.data?.data) {
        const freshDriver = {
          ...res.data.data,
          location: lastPositionsRef.current[driverId],
        };

        // Update popup
        setSelectedDriver(freshDriver);

        // Update global store
        setDrivers((prev) =>
          prev.map((d) => (d.driverId === driverId ? freshDriver : d))
        );

        if (follow) {
          setFollowTargetId(driverId);
        } else {
          setFollowTargetId(null);
        }

        return;
      }
    } catch (err) {
      console.warn("Driver refresh failed, using cached data.");
      const fallback = drivers.find((d) => d.driverId === driverId);
      setSelectedDriver(fallback || null);
    }
  };

  /** ---------------- SMOOTH ANIMATION ---------------- **/
  const animate = (oldPos, newPos, duration, updateFn) => {
    const start = performance.now();
    const tick = (ts) => {
      const progress = Math.min((ts - start) / duration, 1);
      updateFn({
        lat: oldPos.lat + (newPos.lat - oldPos.lat) * progress,
        long: oldPos.long + (newPos.long - oldPos.long) * progress,
      });
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  /** ---------------- SOCKET: LOCATION UPDATES ---------------- **/
  useEffect(() => {
    const handleLocation = (data) => {
      const { lat, long } = data?.location || {};
      if (isNaN(lat) || isNaN(long)) return;

      const newPos = { lat: lat, long: long };
      const driverId = data?.driverId;
      const route = data?.route;
      const progress = data?.progress;

      setDrivers((prev) => {
        const idx = prev.findIndex((d) => d.driverId === driverId);

        if (idx === -1) {
          fetchSingleDriver(driverId);
          return prev;
        }

        const updated = [...prev];
        const oldPos = lastPositionsRef.current[driverId] || newPos;
        lastPositionsRef.current[driverId] = newPos;

        let driver = { ...updated[idx] };

        updated[idx] = driver;

        animate(oldPos, newPos, 450, (pos) => {
          setDrivers((curr) => {
            const list = [...curr];
            if (!list[idx]) return curr;

            list[idx] = {
              ...list[idx],
              location: pos,
              route: route,
              progress: progress,
              routeKey: Date.now(),
            };

            if (selectedDriver?.driverId === driverId) {
              setSelectedDriver(list[idx]);
            }

            return list;
          });
        });

        return updated;
      });
    };

    socket.on("admin:driverLocationUpdate", handleLocation);
    return () => socket.off("admin:driverLocationUpdate", handleLocation);
    // eslint-disable-next-line
  }, [selectedDriver, followTargetId]);

  /** ---------------- SOCKET: ORDER UPDATE → REFRESH DRIVER ---------------- **/
  useEffect(() => {
    const handleOrderUpdate = (data) => {
      if (!data) return;
      if (data?.driverId) fetchSingleDriver(data.driverId);
      if (!data?.driverId) fetchDrivers();
    };

    socket.on("merchant:ordersUpdate", handleOrderUpdate);
    return () => socket.off("merchant:ordersUpdate", handleOrderUpdate);
    // eslint-disable-next-line
  }, []);

  /** ---------------- POPUP SYNC ---------------- **/
  useEffect(() => {
    if (!selectedDriver) return;
    const updated = drivers.find((d) => d.driverId === selectedDriver.driverId);
    if (updated) setSelectedDriver(updated);
    // eslint-disable-next-line
  }, [drivers]);

  /** ---------------- INITIAL LOAD ---------------- **/
  useEffect(() => {
    fetchDrivers();
    // eslint-disable-next-line
  }, []);

  /** ---------------- CLOSE POPUP IF DRIVER HIDDEN OR REMOVED ---------------- **/
  useEffect(() => {
    if (!selectedDriver) return;

    const match = drivers.find((d) => d.driverId === selectedDriver.driverId);

    // Driver removed (offline)
    if (!match) {
      setSelectedDriver(null);
      setFollowTargetId(null);
      return;
    }
    // eslint-disable-next-line
  }, [drivers]);

  return { drivers, updateSelectedDriver, followTargetId };
};

export default useDrivers;
